import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, ShieldCheck, Loader2 } from "lucide-react";
import HealthTwin from "@/components/silentscan/HealthTwin";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TwinShare() {
  const { shareId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/twin/share/${shareId}`);
        setData(d);
      } catch (e) {
        setError(e?.response?.data?.detail || "Share not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(0,229,255,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(52,211,153,0.15) 0%, transparent 60%)",
        }}
      />

      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="twin-share-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <div className="hidden sm:flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <ShieldCheck className="w-3.5 h-3.5 text-cyan-300" />
            Educational · Not a diagnosis
          </div>
          <Link
            to="/dashboard"
            data-testid="twin-share-cta"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Try yours
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-12">
        {loading ? (
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading Twin…
          </div>
        ) : error ? (
          <div
            data-testid="twin-share-error"
            className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 text-red-200"
          >
            {error}
          </div>
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
                Shared Living Portrait
              </div>
              <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
                {data?.name ? data.name : "A friend"}&apos;s{" "}
                <span className="italic text-cyan-300">Health Twin.</span>
              </h1>
              <p className="mt-4 text-zinc-300 max-w-xl leading-relaxed">
                Six dimensions of wellbeing — Mind, Body, Nutrition, Sleep,
                Stress, Hydration — from a preventive AI check-in.
              </p>
              <div className="mt-6 flex items-baseline gap-3">
                <div className="font-display text-6xl font-semibold text-cyan-300 tabular-nums">
                  {data?.overall_score || 0}
                </div>
                <div className="text-sm text-zinc-500">/100 overall</div>
              </div>
            </motion.div>

            <div data-testid="twin-share-avatar" className="mt-4">
              <HealthTwin
                twinScores={data?.twin_scores}
                twinInsights={data?.twin_insights}
                overallScore={data?.overall_score || 0}
              />
            </div>

            <div className="mt-8 rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B3D91]/40 to-cyan-500/10 p-8 sm:p-10 text-center">
              <Sparkles className="w-6 h-6 text-cyan-300 mx-auto" />
              <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold">
                Curious about yours?
              </h2>
              <p className="mt-2 text-zinc-300 max-w-md mx-auto">
                Take the 3-minute preventive check-in and meet your own Health
                Twin. Free. Private. In your kitchen&apos;s language.
              </p>
              <div className="mt-5">
                <Link
                  to="/dashboard"
                  data-testid="twin-share-open-dashboard"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black font-medium text-sm hover:bg-cyan-100"
                >
                  Open my dashboard
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
